import React, { useReducer, useEffect } from "react";
import { InputText } from "../../_components/InputText/InputText";
import styles from "../../_components/Styles/BaseStyleForAdminPanel.module.css";
import OpenCheckboxList from "../../_components/OpenCheckboxList/OpenCheckboxList";

interface User {
    id: string;
    firstName: string;
    lastName: string;
    middleName: string;
    login: string;
    email: string;
    position: string[];
    rights: string[];
}

interface UserState {
    firstName: string;
    lastName: string;
    middleName: string;
    login: string;
    email: string;
    password: string;
    position: string[];
    rights: string[];
}

interface UpdateUserModalProps {
    user: User | null;
    positionOptions: { value: string; label: string }[];
    rightsOptions: { value: string; label: string }[];
    onClose: () => void;
    onSave: (userData: UserState) => void;
}

export const UpdateUserModal = ({ user, positionOptions, rightsOptions, onClose, onSave }: UpdateUserModalProps) => {
    const initialState: UserState = {
        firstName: "",
        lastName: "",
        middleName: "",
        login: "",
        email: "",
        password: "",
        position: [],
        rights: [],
    };

    const reducer = (state: UserState, action: any) => {
        switch (action.type) {
            case "SET_FIELD":
                return { ...state, [action.name]: action.value };
            case "SET_INITIAL":
                return { ...action.state };
            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        if (user) {
            dispatch({
                type: "SET_INITIAL",
                state: {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    middleName: user.middleName,
                    login: user.login,
                    email: user.email,
                    password: "",
                    position: user.position || [],
                    rights: user.rights || [],
                },
            });
        }
    }, [user]);

    const handleValue = (event: any) => {
        dispatch({ type: "SET_FIELD", name: event.target.name, value: event.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(state);
        onClose();
    };

    if (!user) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderContent}>
                        <h2 className={styles.modalTitle}>Обновление пользователя</h2>
                        <p className={styles.modalSubtitle}>Измените данные и сохраните изменения</p>
                    </div>
                    <button
                        type="button"
                        className={styles.modalClose}
                        onClick={onClose}
                        aria-label="Закрыть"
                    >
                        ×
                    </button>
                </div>
                <hr className={styles.modalDivider} />

                <form onSubmit={handleSubmit}>
                    <div className={styles.modalBody}>
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}>Основная информация</h3>
                            </div>
                            <div className={styles.inputsGrid}>
                                <InputText
                                    name="firstName"
                                    value={state.firstName}
                                    handleValue={handleValue}
                                    label="Имя"
                                    idField="firstName"
                                    required={true}
                                />
                                <InputText
                                    name="middleName"
                                    value={state.middleName}
                                    handleValue={handleValue}
                                    label="Фамилия"
                                    idField="middleName"
                                    required={true}
                                />
                                <div className={styles.fullRow}>
                                    <InputText
                                        name="lastName"
                                        value={state.lastName}
                                        handleValue={handleValue}
                                        label="Отчество"
                                        idField="lastName"
                                        required={true}
                                    />
                                </div>
                            </div>
                            <hr className={styles.sectionDivider} />
                        </div>

                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}>Учетные данные</h3>
                            </div>
                            <div className={styles.inputsGrid}>
                                <InputText
                                    name="login"
                                    value={state.login}
                                    handleValue={handleValue}
                                    label="Логин"
                                    idField="login"
                                    required={true}
                                />
                                <InputText
                                    name="email"
                                    value={state.email}
                                    handleValue={handleValue}
                                    label="Почта"
                                    idField="email"
                                    required={true}
                                />
                                <div className={styles.fullRow}>
                                    <InputText
                                        name="password"
                                        value={state.password}
                                        handleValue={handleValue}
                                        label="Пароль (оставьте пустым, если не меняете)"
                                        idField="password"
                                        typeFiled="password"
                                        required={false}
                                    />
                                </div>
                            </div>
                            <hr className={styles.sectionDivider} />
                        </div>

                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}>Роли и доступы</h3>
                            </div>
                            <OpenCheckboxList
                                label="Должности"
                                name="position"
                                options={positionOptions}
                                selected={state.position as string[]}
                                onChange={handleValue}
                                searchValue={""}
                                onSearchChange={() => {}}
                            />

                            <OpenCheckboxList
                                label="Права"
                                name="rights"
                                options={rightsOptions}
                                selected={state.rights as string[]}
                                onChange={handleValue}
                                searchValue={""}
                                onSearchChange={() => {}}
                            />
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            className={`${styles.buttonBase} ${styles.buttonSecondary}`}
                            onClick={onClose}
                        >
                            Отмена
                        </button>

                        <button
                            type="submit"
                            className={`${styles.buttonBase} ${styles.buttonPrimary}`}
                        >
                            Сохранить
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdateUserModal;

